import React, { FC, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useInventoryApi, useInventoryHelpers } from '~Components/InventoryUniverse';
import { Table, TableBody, TableCell, TableHead, TableRow } from '~Components/Table';
import { useI18n } from '~Hooks';
import {
  GetAllVariations,
  GetAllVariationsVariables,
  GetAllVariations_venue_company_variations as Variation,
} from './__queries__';
import { RouteComponentProps } from 'react-router';
import { Box, Button, Card } from '~Styles';
import styled from '@emotion/styled';

const GET_ALL_VARIATIONS = gql`
  query GetAllVariations($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id
        variations {
          id
          name
          nameLang {
            id
            se
            en
            es
            no
          }
          allergen
        }
      }
    }
  }
`;

const IndicatorBall = styled(Box)`
  border-radius: 50%;
  width: 12px;
  height: 12px;
`;

/* type Props = RouteComponentProps<{ id: string }>;
 */
interface Props extends RouteComponentProps<{ id: string }> {
  search?: string;
}

const IngredientInventory: FC<Props> = ({ match, search = '' }) => {
  const venueId = match.params.id;
  const { i18n } = useI18n();
  const inventoryApi = useInventoryApi(venueId);
  const { itemIsOutOfStock } = useInventoryHelpers();

  // Get all variations from the database
  const { data: variationsData, loading, error } = useQuery<
    GetAllVariations,
    GetAllVariationsVariables
  >(GET_ALL_VARIATIONS, {
    variables: { venueId: venueId },
  });

  // Filter variations by search query
  const filteredVariations = useMemo(() => {
    if (!variationsData?.venue?.company?.variations) return [];

    // Get variations that are not allergens
    const variations = variationsData.venue.company.variations
      .filter((variation) => !variation.allergen)
      .map((variation) => ({
        ...variation,
        isOutOfStock: itemIsOutOfStock(variation.id),
      }));

    // Apply search filter if query exists
    if (!search) return variations;

    const query = search.toLowerCase();
    return variations.filter((variation) => variation.name.toLowerCase().includes(query));
  }, [variationsData, itemIsOutOfStock, search]);

  // Handle setting a variation out of stock
  const handleSetVariationStock = (variationId: string, isOutOfStock: boolean) => {
    inventoryApi.setOutOfStock(variationId, !isOutOfStock);
  };

  return (
    <Card padding="xs">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: '50%' }}>Name</TableCell>
            <TableCell>Stock</TableCell>
            <TableCell collapsed>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && <TableRow>Loading...</TableRow>}
          {error && <TableRow>Error: {error.message}</TableRow>}
          {!filteredVariations.length && !loading && (
            <TableRow>
              <TableCell colSpan={3}>
                {search
                  ? `${i18n('Inventory.NoIngredientsFoundMatching')} "${search}"`
                  : `${i18n('Inventory.NoIngredientsFound')}`}
              </TableCell>
            </TableRow>
          )}
          {filteredVariations.map((variation) => (
            <TableRow key={variation.id}>
              <TableCell>{i18n(variation.nameLang)}</TableCell>
              <TableCell>
                <Box display="inline-flex" alignItems="center">
                  <IndicatorBall
                    backgroundColor={
                      variation.isOutOfStock ? 'colorStatusRed' : 'colorStatusGreen'
                    }
                    marginRight="xs"
                  />
                  {variation.isOutOfStock
                    ? `${i18n('Inventory.OutOfStock')}`
                    : `${i18n('Inventory.InStock')}`}
                </Box>
              </TableCell>
              <TableCell collapsed>
                <Button
                  background="colorBlack"
                  color="colorBlueGray50"
                  fluid
                  onTouchEnd={() =>
                    handleSetVariationStock(variation.id, variation.isOutOfStock || false)
                  }>
                  {variation.isOutOfStock
                    ? `${i18n('Inventory.RemoveOutOfStock')}`
                    : `${i18n('Inventory.SetOutOfStock')}`}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

export default IngredientInventory;
